import {
  PaymentMethodCreateParams,
  StripeCardElementChangeEvent,
} from '@stripe/stripe-js';

import * as api from '../../../api';
import * as store from '../../../reducers/store';
import * as logger from '../../../logger';

export type BillingDetails = PaymentMethodCreateParams.BillingDetails;
type Address = PaymentMethodCreateParams.BillingDetails.Address;

const { Primitive } = api.PrevisitFormTemplateFieldConfig;

const minDate = new Date(0);
type AnswerDate = [string | undefined, Date];

export function mergeBillingDetails(...args: BillingDetails[]): BillingDetails {
  const merged: BillingDetails = {};
  const address = {};
  for (const details of args) {
    Object.assign(merged, details);
    Object.assign(address, details.address);
  }
  merged.address = address;
  return merged;
}

function joinExisting(
  items: Array<string | undefined>,
  delimiter: string,
): string {
  return items.filter((n) => !!n).join(delimiter);
}

function convertAddressAnswerToBillingAddress(addressAnswer: string): Address {
  try {
    const place: api.Places.PlaceDetails = JSON.parse(addressAnswer);
    const line1 = joinExisting([place.addressOne, place.addressTwo], ', ');
    return {
      line1,
      city: place.city,
      postal_code: place.zipCode,
      state: place.state,
    };
  } catch {
    logger.error(
      new Error(`Failed to parse address from answer: '${addressAnswer}'`),
    );
  }
  return {};
}

export function extractBillingDetailsFromFormAnswers(
  forms: store.Forms,
): BillingDetails {
  let firstNameAnswer: AnswerDate = [undefined, minDate];
  let lastNameAnswer: AnswerDate = [undefined, minDate];
  let patientEmailAnswer: AnswerDate = [undefined, minDate];
  let patientAddressMailingAnswer: AnswerDate = [undefined, minDate];

  for (const form of Object.values(forms)) {
    for (const field of form.fields) {
      const updated = new Date(field.updated);
      switch (field.primitive) {
        case Primitive.PatientFirstName:
          if (firstNameAnswer[1] >= updated) continue;
          firstNameAnswer = [field.value, updated];
          break;
        case Primitive.PatientLastName:
          if (lastNameAnswer[1] >= updated) continue;
          lastNameAnswer = [field.value, updated];
          break;
        case Primitive.PatientEmail:
          if (patientEmailAnswer[1] >= updated) continue;
          patientEmailAnswer = [field.value, updated];
          break;
        case Primitive.PatientAddressMailing:
          if (patientAddressMailingAnswer[1] >= updated) continue;
          patientAddressMailingAnswer = [field.value, updated];
          break;
        default:
          break;
      }
    }
  }

  const [firstName] = firstNameAnswer;
  const [lastName] = lastNameAnswer;
  const [email] = patientEmailAnswer;
  const [addressAnswer] = patientAddressMailingAnswer;

  let name: string | undefined;
  if (firstName || lastName) {
    name = joinExisting([firstName, lastName], ' ');
  }

  let address: Address | undefined;
  if (addressAnswer) {
    address = convertAddressAnswerToBillingAddress(addressAnswer);
  }

  return {
    ...(name && { name }),
    ...(email && { email }),
    ...(address && { address }),
  };
}

export function getFormattedCurrencyAmount(amount: number, currency: string) {
  // NOTE: does not handle zero-decimal currencies
  return (amount / 100).toLocaleString('en-US', {
    style: 'currency',
    currency,
  });
}

export function isUnsupportedCard(
  unsupportedCardBrands: api.Payment.CardBrand[],
  cardBrand: StripeCardElementChangeEvent['brand'],
): boolean {
  return unsupportedCardBrands.some((card) => card === cardBrand.toUpperCase());
}
